import { Item as RadixItem } from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  type ElementType,
  forwardRef,
  type ReactEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { MobileMenuModeContext } from './contexts/MobileMenuModeContext.js';
import { useIsMenuItemHovered } from './hooks/useIsMenuItemHovered.js';
import { useMenuContext } from './hooks/useMenuContext.js';
import { getItemSlots } from './item-slots.js';
import { MenuItemTooltipWrapper } from './ItemTooltip.js';
import * as styles from './Menu.sty.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useTooltipState } from '../../overlays/hooks/useTooltipState.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

/**
 * @public
 */
export interface MenuItemOwnProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId {
  /**
   * Whether the menu item is disabled.
   * If `true`, it prevents the user from interacting with the item and the item
   * being focused.
   */
  disabled?: boolean;
  /**
   * Optional text used for typeahead purposes. By default, the typeahead
   * behavior will use the `.textContent` of the item. Use this when the content
   * is complex, or you have non-textual content inside.
   */
  textValue?: string;
  /*
   * TODO: The onSelect from the Radix MenuItem actually emits a native Event instead of a react synthetic event.
   * When trying to fix that, our the app toolkits version of typescript ~4.9 bailed due to the type complexity. Right now there is no way
   * to correct the typing on the event and keep a compatible type version.
   * This limitiation also tracks through to the `ActionGroup` and `AppHeader`.
   * APPDEV-8522
   */
  /**
   * Event handler called when the user selects an item (via mouse or keyboard).
   * Calling `event.preventDefault()` in this handler prevents the dropdown menu
   * from closing when selecting that item.
   */
  onSelect: ReactEventHandler;
}

/**
 * @public
 */
export type MenuItemProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  MenuItemOwnProps
>;

/**
 * The component that contains the dropdown menu Items.
 * @public
 */
export const Item: <E extends ElementType = 'div'>(
  props: MenuItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      children,
      disabled = false,
      onSelect,
      textValue,
      as = 'div' as E,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MenuItemProps<E>,
    forwardedRef: typeof remainingProps.ref,
  ) => {
    const isMobile = useContext(MobileMenuModeContext);
    const { setIsOpen } = useMenuContext();
    const slots = getItemSlots(children);
    const {
      isOpen,
      open: openTooltip,
      close: closeTooltip,
    } = useTooltipState({
      defaultOpen: false,
    });
    const [tooltipTriggerRef, setTooltipTriggerRef] =
      useState<HTMLElement | null>(null);
    const { focusProps, focusClassName } = useFocusRing({
      disabled,
    });
    const isTooltipHovered = useRef(false);
    const { isItemHovered, hoverProps } = useIsMenuItemHovered();
    const mobileRef = useMergeRefs([forwardedRef, setTooltipTriggerRef]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }
      window.addEventListener('scroll', closeTooltip, { capture: true });
      return () =>
        window.removeEventListener('scroll', closeTooltip, { capture: true });
    }, [isOpen, closeTooltip]);

    if (isMobile) {
      return (
        <>
          <Flex
            ref={mobileRef}
            role="menuitem"
            aria-disabled={disabled}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-details={ariaDetails}
            data-testid={dataTestId}
            className={clsx(
              textStyleCSS({ textStyle: 'base-emphasized' }),
              !isItemHovered && focusClassName,
              styles.item({ disabled }),
              consumerClassName,
            )}
            style={consumerStyle}
            disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            as={as satisfies ElementType}
            {..._mergeProps(focusProps, hoverProps, {
              onClick: (event: MouseEvent) => {
                if (disabled) {
                  event.stopPropagation();
                  event.preventDefault();
                  return;
                }
                onSelect(event);
                if (!event.defaultPrevented) {
                  setIsOpen(false);
                }
              },
              onFocus: () => slots.tooltip && openTooltip(true),
              onBlur: () =>
                slots.tooltip &&
                requestAnimationFrame(() => {
                  if (!isTooltipHovered.current) {
                    closeTooltip();
                  }
                }),
              onMouseEnter: () => slots.tooltip && openTooltip(true),
              onMouseLeave: () =>
                slots.tooltip &&
                requestAnimationFrame(() => {
                  if (!isTooltipHovered.current) {
                    closeTooltip();
                  }
                }),
            })}
            {...remainingProps}
          >
            {slots.prefix}
            <div className={styles.itemChildren}>{slots.freeform}</div>
            {slots.suffix}
          </Flex>
          {isOpen && (
            <MenuItemTooltipWrapper
              triggerElement={tooltipTriggerRef}
              onMouseEnter={() => {
                isTooltipHovered.current = true;
              }}
              onMouseLeave={() => {
                isTooltipHovered.current = false;
                closeTooltip();
              }}
            >
              {slots.tooltip}
            </MenuItemTooltipWrapper>
          )}
        </>
      );
    }

    return (
      <>
        <RadixItem
          data-testid={dataTestId}
          disabled={disabled}
          textValue={textValue}
          ref={forwardedRef}
          {..._mergeProps(focusProps, hoverProps, {
            onClick: (event: MouseEvent) => {
              if (disabled) {
                event.stopPropagation();
                event.preventDefault();
                return;
              }

              const nativeEvent = event.nativeEvent;
              if (
                nativeEvent.button !== 0 ||
                nativeEvent.metaKey ||
                nativeEvent.ctrlKey ||
                nativeEvent.shiftKey
              ) {
                return; // let the browser handle the event
              }

              onSelect(event);
            },
            onFocus: () => slots.tooltip && openTooltip(true),
            onBlur: () =>
              slots.tooltip &&
              // Ensures isTooltipHovered state is set correctly when the tooltip is hovered.
              // Otherwise, the tooltip would close before the onMouseLeave event of the tooltip triggers
              // and the isTooltipHovered state would never be set.
              requestAnimationFrame(() => {
                if (!isTooltipHovered.current) {
                  closeTooltip();
                }
              }),
            className: consumerClassName,
            style: consumerStyle,
          })}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-details={ariaDetails}
          asChild
        >
          <Flex
            {...remainingProps}
            className={clsx(
              textStyleCSS({ textStyle: 'base-emphasized' }),
              !isItemHovered && focusClassName,
              styles.item({ disabled }),
            )}
            disabled={disabled}
            // TODO LeMa: role actually belongs on top-level element (check for side effects regarding tests after moving the attribute!)
            role="menuitem"
            aria-disabled={disabled}
            ref={setTooltipTriggerRef}
            as={as satisfies ElementType}
          >
            {slots.prefix}
            <div className={styles.itemChildren}>{slots.freeform}</div>
            {slots.suffix}
          </Flex>
        </RadixItem>
        {isOpen &&
          createPortal(
            <MenuItemTooltipWrapper
              triggerElement={tooltipTriggerRef}
              onMouseEnter={() => {
                isTooltipHovered.current = true;
              }}
              onMouseLeave={() => {
                isTooltipHovered.current = false;
                closeTooltip();
              }}
            >
              {slots.tooltip}
            </MenuItemTooltipWrapper>,
            document.body,
          )}
      </>
    );
  },
);

(Item as typeof Item & { displayName: string }).displayName = 'Menu.Item';
